import { Collapse, Skeleton, Empty, Typography, Descriptions } from "antd";
import { FormattedMessage } from "react-intl";
import dayjs from "dayjs";
import { XAICall } from "@lib/types/xAI";
import {
  XAICallStatusTag,
  XAIClientMoodTag,
  XAIDisconnectionResultTag,
  CallDuration,
} from "@lib/components/MiniComponents/Others";
import styled from "styled-components";
import { XAICallDrawer } from "@lib/components/PageComponents/XAICall";
import { useState } from "react";
import VoicePlayer from "@lib/components/MiniComponents/Voice";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";

const StyledCollapse = styled(Collapse)`
  background: transparent;
  border-radius: ${({ theme }) => theme.cardRadius};
  border: 1px dashed ${({ theme }) => theme.palette.borderColor};
  background-color: ${({ theme }) =>
    theme.palette.background.secondary} !important;

  .ant-collapse-item {
    margin-bottom: 8px;
  }

  .ant-collapse-header {
    padding: 12px 16px !important;
  }

  .ant-collapse-content-box {
    padding: 0 !important;
    background-color: ${({ theme }) =>
      theme.palette.background.secondary} !important;
  }
`;

type Props = {
  callsData: XAICall[];
  isLoading: boolean;
  onCallClick: (call: XAICall) => void;
};

export const XAILeadCallsList = (props: Props) => {
  const { callsData, isLoading, onCallClick } = props;
  const [selectedCall, setSelectedCall] = useState<XAICall | null>(null);

  if (isLoading) {
    return <Skeleton active paragraph={{ rows: 3 }} />;
  }

  if (callsData.length === 0) {
    return (
      <Empty description={<FormattedMessage id="common.noDataAvailable" />} />
    );
  }

  const collapseItems = callsData.map((call, index) => {
    const callDate = call?.createdAt
      ? dayjs(call.createdAt).format("DD/MM/YYYY HH:mm:ss")
      : `Call ${index + 1}`;

    return {
      key: call.id || `call-${index}`,
      label: (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Typography.Text strong>{callDate}</Typography.Text>
            <XAIClientMoodTag mood={call?.clientMood} size="small" />
            <XAICallStatusTag status={call?.callStatus} size="small" />
          </div>
          <CallDuration durationMs={call?.durationMs} size="small" />
        </div>
      ),
      children: (
        <div>
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label={<FormattedMessage id="common.callId" />}>
              <Typography.Text copyable={{ text: call?.callId }}>
                {call?.callId || "-"}
              </Typography.Text>
            </Descriptions.Item>

            <Descriptions.Item label={<FormattedMessage id="common.agent" />}>
              <Typography.Text>{call?.agentName || "-"}</Typography.Text>
            </Descriptions.Item>

            <Descriptions.Item
              label={<FormattedMessage id="common.duration" />}
            >
              <CallDuration durationMs={call?.durationMs} size="small" />
            </Descriptions.Item>

            <Descriptions.Item
              label={<FormattedMessage id="common.clientMood" />}
            >
              <XAIClientMoodTag mood={call?.clientMood} size="small" />
            </Descriptions.Item>

            <Descriptions.Item
              label={<FormattedMessage id="common.callStatus" />}
            >
              <XAICallStatusTag status={call?.callStatus} size="small" />
            </Descriptions.Item>

            <Descriptions.Item
              label={<FormattedMessage id="common.disconnectionResult" />}
            >
              <XAIDisconnectionResultTag
                result={call?.disconnectionResult}
                size="small"
              />
            </Descriptions.Item>

            <Descriptions.Item
              label={<FormattedMessage id="common.voiceFile" />}
            >
              <VoicePlayer voiceFile={call?.voiceFile} title={false} />
            </Descriptions.Item>

            <Descriptions.Item label={<FormattedMessage id="common.summary" />}>
              <Typography.Text>{call?.summary}</Typography.Text>
            </Descriptions.Item>
          </Descriptions>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <ActionButton
              type="detail"
              onClick={() => {
                onCallClick(call);
              }}
            />
          </div>
        </div>
      ),
    };
  });

  return (
    <>
      <StyledCollapse
        items={collapseItems}
        defaultActiveKey={collapseItems.map((item) => item.key)}
        style={{ background: "transparent" }}
      />

      <XAICallDrawer
        open={!!selectedCall}
        onClose={() => setSelectedCall(null)}
        item={selectedCall}
      />
    </>
  );
};
